import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { api, ApiError } from '../api/client';
import type { Connection } from '../api/client';
import { Alert, EmptyState, Spinner } from '../components/Feedback';
import { StatusPill } from './DashboardPage';

export function DatabasesPage() {
  const [connections, setConnections] = useState<Connection[] | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState('');

  const load = useCallback(() => {
    setConnections(null);
    api.databases
      .list()
      .then(setConnections)
      .catch((e) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const retest = async (id: string) => {
    setBusyId(id);
    setError('');
    setNotice('');
    try {
      const result = await api.databases.retest(id);
      setNotice(result.message);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The connection could not be tested.');
    } finally {
      setBusyId('');
    }
  };

  const remove = async (connection: Connection) => {
    const confirmed = window.confirm(
      `Delete “${connection.name}”?\n\nIts stored password is removed and any MCP token that used it will no longer be able to query it. This cannot be undone.`,
    );
    if (!confirmed) return;

    setBusyId(connection.id);
    setError('');
    try {
      await api.databases.remove(connection.id);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The database could not be deleted.');
    } finally {
      setBusyId('');
    }
  };

  const filtered = (connections ?? []).filter((c) =>
    `${c.name} ${c.database_type} ${c.database_name} ${c.host ?? ''}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">YOUR DATABASES</p>
          <h1>Databases</h1>
          <p className="muted">
            Each database is visible only to your account, and to MCP agents using your tokens.
          </p>
        </div>
        <Link className="primary" to="/databases/new">
          <Plus size={17} /> Add database
        </Link>
      </header>

      {error && <Alert tone="error" onDismiss={() => setError('')}>{error}</Alert>}
      {notice && <Alert tone="success" onDismiss={() => setNotice('')}>{notice}</Alert>}

      {connections === null ? (
        <Spinner label="Loading databases" />
      ) : connections.length === 0 ? (
        <EmptyState
          title="No databases yet"
          description="Add a database so an AI agent can inspect and query it."
          action={
            <Link className="primary" to="/databases/new">
              <Plus size={17} /> Add your first database
            </Link>
          }
        />
      ) : (
        <>
          <div className="toolbar">
            <div className="search">
              <Search size={16} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter databases"
                aria-label="Filter databases"
              />
            </div>
            <span className="muted">
              {filtered.length} of {connections.length}
            </span>
          </div>

          <div className="grid-two">
            {filtered.map((connection) => (
              <article key={connection.id} className="panel db-card">
                <div className="db-head">
                  <div>
                    <h3>{connection.name}</h3>
                    <span className="tag">{connection.database_type}</span>
                  </div>
                  <StatusPill status={connection.status} />
                </div>

                <dl className="db-facts">
                  <div>
                    <dt>Database</dt>
                    <dd>{connection.database_name}</dd>
                  </div>
                  {connection.host && (
                    <div>
                      <dt>Host</dt>
                      <dd>
                        {connection.host}
                        {connection.port ? `:${connection.port}` : ''}
                      </dd>
                    </div>
                  )}
                  {connection.username && (
                    <div>
                      <dt>User</dt>
                      <dd>{connection.username}</dd>
                    </div>
                  )}
                </dl>

                {connection.last_error && (
                  <p className="db-error">{connection.last_error}</p>
                )}

                <footer className="db-actions">
                  <Link className="link" to={`/databases/${connection.id}`}>
                    Inspect schema
                  </Link>
                  <Link className="link" to={`/query?connection=${connection.id}`}>
                    Query
                  </Link>
                  <button
                    className="link"
                    onClick={() => retest(connection.id)}
                    disabled={busyId === connection.id}
                  >
                    <RefreshCw size={14} /> {busyId === connection.id ? 'Testing…' : 'Test'}
                  </button>
                  <button
                    className="link danger"
                    onClick={() => remove(connection)}
                    disabled={busyId === connection.id}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </footer>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
